import { HOLDINGS_EVENT_TYPE_OPTIONS } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/common/utils';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { drawerTransitionProps } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling';
import { Button, Drawer, Textarea, Title } from '@mantine/core';
import { useContext, useEffect, useState } from 'react';
import { FormProvider, useWatch } from 'react-hook-form';
import { MantineDateInput } from '@dealstack/frontend/shared/components/mantineForm/MantineDateInput';
import {
	CREATE_HOLDING_EVENT_FORM_FIELD,
	useHoldingEventDrawerController,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Holdings/HoldingEventDrawer/useHoldingEventDrawerController.ts';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader';
import { HoldingEventRes, HoldingEventType } from '@dealstack/shared/lib/generated';
import { FilePreviewComponent } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Holdings/HoldingEventDrawer/FilePreviewComponent.tsx';
import {
	RegularViewTableItemData,
	SimpleRegularViewTableItemData,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Holdings/RegularViewTable/useRegularViewTableController.ts';
import {
	DocumentValue,
	EditFormCard,
	EventCardsContainer,
	EventCardWrapper,
	EventContent,
	EventField,
	EventForm,
	EventHeader,
	FieldLabel,
	FieldValue,
	HeaderTitle,
	UnitsTitle,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Holdings/HoldingEventDrawer/styled.tsx';
import {
	eventTypes,
	findEventByType,
	getDateLabel,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Holdings/HoldingEventDrawer/utils';
import {
	DATE_FORMATS,
	formatBEDateToUiWithFallback,
} from '@dealstack/shared/lib/format/dates/date.ts';
import { EntitySelectComponent as HoldingEntitySelect } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Holdings/HoldingEventDrawer/EntitySelectComponent.tsx';
import { PledgeAgainstSelectComponent as PledgedAgainstSelect } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Holdings/HoldingEventDrawer/PledgeAgainstSelectComponent.tsx';
import { FileUploadComponent } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Holdings/HoldingEventDrawer/FileUploadComponent.tsx';
import { browserDateToMidnightUTC } from '@dealstack/frontend/shared/utils/utc.ts';
import { PdfViewerModal } from '@dealstack/frontend/shared/components/DocumentViewerV2/PdfViewer/PdfViewerModal';
import { useMutation } from '@tanstack/react-query';
import { DocumentService } from '@dealstack/frontend/shared/dataExchange/services/document/document.service.ts';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2.tsx';
import { DealContext } from '@dealstack/frontend/app/pages/Project/DealDashboard/DealContext';

interface Props {
	holding?: RegularViewTableItemData;
	className?: string;
	opened: boolean;
	onClose: (createdEventId?: string) => void;
	refreshSecuritiesList: () => void;
	holdingsEvent: HoldingEventRes[];
	setModalFileId?: (fileId: string | undefined) => void;
	modalFileId?: string;
	debtHoldings?: SimpleRegularViewTableItemData[];
}

export const EditHoldingEventDrawer = ({
	holding,
	opened,
	onClose,
	className,
	refreshSecuritiesList,
	holdingsEvent = [],
	debtHoldings = [],
}: Props) => {
	const tCapManage = useTranslationCapManage();
	const [activeEditType, setActiveEditType] = useState<HoldingEventType | null>(null);
	const { dealId } = useContext(DealContext);
	const [modalOpen, setModalOpen] = useState(false);
	const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
	const [currentFileName, setCurrentFileName] = useState<string | undefined>(undefined);
	const [isDrawerVisible, setIsDrawerVisible] = useState(true);

	const getDownloadPreSignedUrl = useMutation({
		mutationFn: DocumentService.getDownloadPreSignedUrl,
	});

	const issuanceEvent = findEventByType(
		holdingsEvent,
		HoldingEventType.ISSUANCE_OF_SHARE_CERTIFICATES
	);
	const pledgeEvent = findEventByType(holdingsEvent, HoldingEventType.PLEDGING_OF_SHARES);
	const assignmentEvent = findEventByType(
		holdingsEvent,
		HoldingEventType.ASSIGNMENT_OF_VOTING_RIGHTS
	);
	const noteEvent = findEventByType(holdingsEvent, HoldingEventType.NOTE);

	const eventMap = {
		[HoldingEventType.ISSUANCE_OF_SHARE_CERTIFICATES]: issuanceEvent,
		[HoldingEventType.PLEDGING_OF_SHARES]: pledgeEvent,
		[HoldingEventType.ASSIGNMENT_OF_VOTING_RIGHTS]: assignmentEvent,
		[HoldingEventType.NOTE]: noteEvent,
	};

	const {
		editHoldingsEventForm,
		onEditSubmit,
		uploadedFile,
		setUploadedFile,
		removeFile,
		updateHoldingsEventRequest,
		handleRemoveEventDocument,
		entitiesSelectOptions,
	} = useHoldingEventDrawerController(
		refreshSecuritiesList,
		onClose,
		holding,
		activeEditType && eventMap[activeEditType] ? [eventMap[activeEditType]] : []
	);

	const [localEntitiesOptions, setLocalEntitiesOptions] = useState(entitiesSelectOptions);

	useEffect(() => {
		setLocalEntitiesOptions(entitiesSelectOptions);
	}, [entitiesSelectOptions]);

	const documentName = useWatch({
		control: editHoldingsEventForm.control,
		name: CREATE_HOLDING_EVENT_FORM_FIELD.documentName,
	});

	const documentUrl = useWatch({
		control: editHoldingsEventForm.control,
		name: CREATE_HOLDING_EVENT_FORM_FIELD.documentUrl,
	});

	const fileId = useWatch({
		control: editHoldingsEventForm.control,
		name: CREATE_HOLDING_EVENT_FORM_FIELD.fileId,
	});

	const dateLabel = activeEditType && getDateLabel(activeEditType, tCapManage);

	const handleEditClick = (type: HoldingEventType) => {
		setActiveEditType(type);

		const event = eventMap[type];

		if (event) {
			editHoldingsEventForm.reset({
				[CREATE_HOLDING_EVENT_FORM_FIELD.id]: event.id,
				[CREATE_HOLDING_EVENT_FORM_FIELD.date]: event.date || '',
				[CREATE_HOLDING_EVENT_FORM_FIELD.type]: type,
				[CREATE_HOLDING_EVENT_FORM_FIELD.note]: event.note || '',
				[CREATE_HOLDING_EVENT_FORM_FIELD.pledgeToPersonId]: event.pledgeToPersonId ?? null,
				[CREATE_HOLDING_EVENT_FORM_FIELD.pledgeToString]: event.pledgeToString || '',
				[CREATE_HOLDING_EVENT_FORM_FIELD.pledgeFromHolderId]:
					event.pledgeFromHolderId ?? null,
				[CREATE_HOLDING_EVENT_FORM_FIELD.pledgeFromSecurityId]:
					event.pledgeFromSecurityId ?? null,
				[CREATE_HOLDING_EVENT_FORM_FIELD.pledgeFromString]: event.pledgeFromString || '',
				[CREATE_HOLDING_EVENT_FORM_FIELD.assignedToPersonId]:
					event.assignedToPersonId ?? null,
				[CREATE_HOLDING_EVENT_FORM_FIELD.assignedToString]: event.assignedToString || '',
				[CREATE_HOLDING_EVENT_FORM_FIELD.documentUrl]: event.documentUrl || '',
				[CREATE_HOLDING_EVENT_FORM_FIELD.documentName]: event.documentName || '',
				[CREATE_HOLDING_EVENT_FORM_FIELD.fileId]: event.fileId || '',
			});
		}
	};

	const pledgeToStringValue = useWatch({
		control: editHoldingsEventForm.control,
		name: CREATE_HOLDING_EVENT_FORM_FIELD.pledgeToString,
	});

	useEffect(() => {
		if (
			pledgeToStringValue &&
			!localEntitiesOptions.some((option) => option.value === pledgeToStringValue)
		) {
			setLocalEntitiesOptions((prev) => [
				...prev,
				{ value: pledgeToStringValue, label: pledgeToStringValue },
			]);
		}
	}, [pledgeToStringValue, localEntitiesOptions]);

	const assignedToStringValue = useWatch({
		control: editHoldingsEventForm.control,
		name: CREATE_HOLDING_EVENT_FORM_FIELD.assignedToString,
	});

	useEffect(() => {
		if (
			assignedToStringValue &&
			!localEntitiesOptions.some((option) => option.value === assignedToStringValue)
		) {
			setLocalEntitiesOptions((prev) => [
				...prev,
				{ value: assignedToStringValue, label: assignedToStringValue },
			]);
		}
	}, [assignedToStringValue, localEntitiesOptions]);

	const handleCancelEdit = () => {
		setActiveEditType(null);
	};

	const handleRemoveDocument = () => {
		if (activeEditType && eventMap[activeEditType]?.id) {
			void handleRemoveEventDocument(eventMap[activeEditType].id);
			editHoldingsEventForm.setValue(CREATE_HOLDING_EVENT_FORM_FIELD.documentUrl, undefined, {
				shouldDirty: true,
				shouldValidate: true,
			});
			editHoldingsEventForm.setValue(
				CREATE_HOLDING_EVENT_FORM_FIELD.documentName,
				undefined,
				{
					shouldDirty: true,
					shouldValidate: true,
				}
			);

			void editHoldingsEventForm.trigger();
		} else {
			editHoldingsEventForm.setValue(CREATE_HOLDING_EVENT_FORM_FIELD.documentUrl, undefined, {
				shouldDirty: true,
				shouldValidate: true,
			});
			editHoldingsEventForm.setValue(
				CREATE_HOLDING_EVENT_FORM_FIELD.documentName,
				undefined,
				{
					shouldDirty: true,
					shouldValidate: true,
				}
			);

			removeFile();
			void editHoldingsEventForm.trigger();
		}
	};

	const handleDocumentPreview = (fileId: string, fileName: string) => {
		if (!fileId || !dealId) {
			return;
		}

		setCurrentFileName(fileName);
		setIsDrawerVisible(false);

		getDownloadPreSignedUrl.mutate(
			{
				dealId,
				fileId,
			},
			{
				onSuccess: async (data) => {
					if (data.payload?.downloadUrl) {
						try {
							const response = await fetch(data.payload.downloadUrl);
							const blob = await response.blob();

							setPdfBlob(blob);
							setModalOpen(true);
						} catch {
							showToastV2({
								title: tCapManage('holdings.event.document_error'),
								state: 'error',
							});
						}
					}
				},
				onError: () => {
					setIsDrawerVisible(true);
				},
			}
		);
	};

	const handleCloseModal = () => {
		setModalOpen(false);
		setPdfBlob(null);
		setIsDrawerVisible(true);
	};

	const getDocumentComponent = () => {
		if (documentName && documentUrl && !uploadedFile) {
			return (
				<FilePreviewComponent
					fileName={documentName}
					onRemove={handleRemoveDocument}
					setIsDrawerVisible={setIsDrawerVisible}
					fileId={fileId}
				/>
			);
		}

		if (uploadedFile) {
			return (
				<FilePreviewComponent
					fileName={uploadedFile.name}
					onRemove={removeFile}
					setIsDrawerVisible={setIsDrawerVisible}
					fileId={fileId}
				/>
			);
		}

		return <FileUploadComponent setUploadedFile={setUploadedFile} inputId="fileUploadEdit" />;
	};

	const documentComponent = getDocumentComponent();

	const renderEventCard = (type: HoldingEventType) => {
		const event = eventMap[type];

		if (!event) {
			return null;
		}

		const typeOption = HOLDINGS_EVENT_TYPE_OPTIONS.find((option) => option.value === type);
		const label = typeOption?.label || type;

		if (activeEditType === type) {
			return (
				<EditFormCard key={type}>
					<EventHeader>
						<HeaderTitle>{label}</HeaderTitle>
						<Button
							variant="text"
							style={{ color: '#0D5EF2' }}
							onClick={handleCancelEdit}
						>
							{tCapManage('holdings.event.cancel')}
						</Button>
					</EventHeader>

					<EventForm>
						<MantineDateInput
							name={CREATE_HOLDING_EVENT_FORM_FIELD.date}
							label={dateLabel}
							size="md"
							onChange={(value) => {
								const isoDate = browserDateToMidnightUTC(value);
								if (!isoDate) {
									return;
								}

								editHoldingsEventForm.setValue(
									CREATE_HOLDING_EVENT_FORM_FIELD.date,
									isoDate
								);
							}}
						/>

						{activeEditType === HoldingEventType.PLEDGING_OF_SHARES && (
							<>
								<HoldingEntitySelect
									name={CREATE_HOLDING_EVENT_FORM_FIELD.pledgeToString}
									personIdFieldName={
										CREATE_HOLDING_EVENT_FORM_FIELD.pledgeToPersonId
									}
									label={tCapManage('holdings.event.pledge_to')}
									placeholder={tCapManage('holdings.event.pledge_person')}
									options={localEntitiesOptions}
									setOptions={setLocalEntitiesOptions}
								/>
								<PledgedAgainstSelect
									debtHoldings={debtHoldings}
									defaultValue={event?.pledgeFromString}
									formFields={{
										pledgeFromString:
											CREATE_HOLDING_EVENT_FORM_FIELD.pledgeFromString,
										pledgeFromHolderId:
											CREATE_HOLDING_EVENT_FORM_FIELD.pledgeFromHolderId,
										pledgeFromSecurityId:
											CREATE_HOLDING_EVENT_FORM_FIELD.pledgeFromSecurityId,
									}}
								/>
							</>
						)}

						{activeEditType === HoldingEventType.ASSIGNMENT_OF_VOTING_RIGHTS && (
							<HoldingEntitySelect
								name={CREATE_HOLDING_EVENT_FORM_FIELD.assignedToString}
								personIdFieldName={
									CREATE_HOLDING_EVENT_FORM_FIELD.assignedToPersonId
								}
								label={tCapManage('holdings.event.assigned_to')}
								placeholder={tCapManage('holdings.event.pledge_to')}
								options={localEntitiesOptions}
								setOptions={setLocalEntitiesOptions}
							/>
						)}

						<Textarea
							{...editHoldingsEventForm.register(
								CREATE_HOLDING_EVENT_FORM_FIELD.note
							)}
							label={tCapManage('holdings.event.note_to')}
							placeholder={tCapManage('holdings.event.note_desc')}
							size="md"
							autosize
							minRows={4}
						/>

						<UnitsTitle>{tCapManage('holdings.event.document')}</UnitsTitle>
						{documentComponent}

						<Button fullWidth type="submit">
							{tCapManage('holdings.event.update')}
						</Button>
					</EventForm>
				</EditFormCard>
			);
		}

		return (
			<EventCardWrapper key={type}>
				<EventHeader>
					<HeaderTitle>{label}</HeaderTitle>
					<Button
						variant="text"
						style={{ color: '#0D5EF2' }}
						onClick={() => handleEditClick(type)}
					>
						{tCapManage('holdings.event.edit_single')}
					</Button>
				</EventHeader>

				<EventContent>
					<EventField>
						<FieldLabel>{getDateLabel(type, tCapManage)}:</FieldLabel>
						<FieldValue>
							{formatBEDateToUiWithFallback(event.date, DATE_FORMATS.CALENDAR_DATE)}
						</FieldValue>
					</EventField>

					{type === HoldingEventType.PLEDGING_OF_SHARES && event.pledgeToString && (
						<EventField>
							<FieldLabel>{tCapManage('holdings.event.pledge_to')}:</FieldLabel>
							<FieldValue>{event.pledgeToString}</FieldValue>
						</EventField>
					)}

					{type === HoldingEventType.ASSIGNMENT_OF_VOTING_RIGHTS &&
						event.assignedToString && (
							<EventField>
								<FieldLabel>{tCapManage('holdings.event.assigned_to')}:</FieldLabel>
								<FieldValue>{event.assignedToString}</FieldValue>
							</EventField>
						)}

					{event.note && (
						<EventField>
							<FieldLabel>{tCapManage('holdings.event.note_to')}:</FieldLabel>
							<FieldValue>{event.note}</FieldValue>
						</EventField>
					)}

					{event.documentName && event.fileId && (
						<EventField>
							<FieldLabel>{tCapManage('holdings.event.document')}:</FieldLabel>
							<DocumentValue
								onClick={() =>
									handleDocumentPreview(
										event.fileId || '',
										event.documentName || ''
									)
								}
							>
								{event.documentName}
							</DocumentValue>
						</EventField>
					)}
				</EventContent>
			</EventCardWrapper>
		);
	};

	return (
		<>
			<Drawer
				opened={opened}
				onClose={onClose}
				position="right"
				size="md"
				className={className}
				transitionProps={drawerTransitionProps}
				trapFocus={false}
				style={{ visibility: isDrawerVisible ? 'visible' : 'hidden' }}
				title={
					<Title order={4} fw={600}>
						{tCapManage('holdings.event.edit')}
					</Title>
				}
			>
				{updateHoldingsEventRequest.isPending && <InlineLoader />}

				<FormProvider {...editHoldingsEventForm}>
					<form onSubmit={editHoldingsEventForm.handleSubmit(onEditSubmit())}>
						<EventCardsContainer>
							{eventTypes.map((type) => renderEventCard(type))}
						</EventCardsContainer>
					</form>
				</FormProvider>
			</Drawer>

			<PdfViewerModal
				opened={modalOpen}
				onClose={handleCloseModal}
				pdfViewerProps={{
					fileName: currentFileName || 'Document',
					file: pdfBlob || undefined,
					isLoading: getDownloadPreSignedUrl.isPending,
				}}
			/>
		</>
	);
};
